import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { paysByLink, showsSubtitle } from "@majourneys/shared";
import { Breadcrumbs, type Crumb } from "../internal-ui/breadcrumbs";
import { PW, pewterButton } from "../internal-ui/pewter-shell";
import { useSaveToast } from "../internal-ui/save-toast";
import { ActivityGallery, type ActivityGalleryMap, type ActivityGalleryVideo } from "./activity-gallery";
import type { VideoModalCta } from "../internal-ui/video-modal";
import {
  SAVED_CHANGED_EVENT,
  SAVED_STORAGE_KEY,
  isSaved as readIsSaved,
  toggleSavedItem,
} from "../lib/local-saved";

/** A subtitle this short reads as a highlight in the facts line. */
const HIGHLIGHT_MAX = 60;

export type TopPayment = "free" | "online" | null;

/**
 * What the facts line says about money.
 *
 * - Explore listings take no booking through us, so they make no claim.
 * - Stays, and the camps paid by an emailed link, are paid online in full.
 * - Restaurants say "Free to reserve": a table costs nothing to book.
 * - Nothing else says anything (owner, 2026-10-15: "free to reserve keep it
 *   only in restaurants"). A paused venue takes no booking either.
 */
export function topPaymentKind(input: {
  slug: string;
  isExplore: boolean;
  isStay: boolean;
  isNightclub: boolean;
  isRestaurant?: boolean;
  bookingPaused?: boolean | null;
}): TopPayment {
  if (input.isExplore) return null;
  if (input.isStay || paysByLink(input.slug)) return "online";
  if (input.isNightclub || input.bookingPaused) return null;
  return input.isRestaurant ? "free" : null;
}

/**
 * Split the venue's own data into the facts line's highlight and the rest.
 * Nothing is invented: a short subtitle, else the first fact, is the
 * highlight. `rest` is what is left for the lower fact tiles, so nothing
 * shows twice. The fact chips under the photos are gone (owner, 2026-11-01).
 */
export function splitTopFacts(input: {
  title: string;
  subtitle?: string | null;
  facts?: readonly string[] | null;
}): { highlight: string | null; subtitleUsed: boolean; rest: string[] } {
  const facts = (input.facts ?? []).filter((f): f is string => typeof f === "string" && f.trim().length > 0);
  const subtitle = input.subtitle?.trim() ?? "";
  const shortSubtitle =
    showsSubtitle(input.title, subtitle) && subtitle.length <= HIGHLIGHT_MAX ? subtitle : null;
  if (shortSubtitle) return { highlight: shortSubtitle, subtitleUsed: true, rest: facts };
  const [first, ...others] = facts;
  return { highlight: first ?? null, subtitleUsed: false, rest: others };
}

/** Menu, Share and Save: the soft-bevel white face over one hairline. */
const CONTROL_STYLE: CSSProperties = {
  ...pewterButton,
  color: PW.navy,
};

export interface ActivityPageTopProps {
  slug: string;
  title: string;
  subtitle?: string | null;
  cityName: string;
  categoryName?: string | null;
  images: string[];
  facts?: readonly string[] | null;
  price: number;
  priceType?: number;
  crumbs: Crumb[];
  payment: TopPayment;
  /** Signed-in wishlist state. When undefined, Save uses this browser's list. */
  wishlisted?: boolean;
  onToggleWishlist?: () => void;
  /** The page's share modal, opened when the native share sheet cannot be. */
  renderShareModal?: (props: {
    url: string;
    title: string;
    thumbnail?: string;
    cityName: string;
    price: number;
    onClose: () => void;
  }) => ReactNode;
  /**
   * A restaurant with a menu: shows a Menu button beside the name that jumps
   * to the menu section. Omit when there is no menu to jump to.
   */
  onMenu?: () => void;
  /** Sits beside Share and Save from `lg` up, e.g. "Book a table". */
  titleAction?: ReactNode;
  /** Under the facts line, e.g. the restaurant's open-now pill. */
  children?: ReactNode;
  /** Wraps the photos, so the page can observe them. */
  galleryRef?: Ref<HTMLDivElement>;
  /** The venue's published reel: adds "Watch video" on the photos. */
  video?: ActivityGalleryVideo | null;
  /** The video's full screen: line under the name, description, button. */
  videoMeta?: string | null;
  videoDescription?: string | null;
  videoCta?: VideoModalCta | null;
  /** The venue's pin: adds "See the map" on the desktop photos. */
  map?: ActivityGalleryMap | null;
}

/**
 * The top of an activity page (lab concept 37): the name with Share and Save,
 * one line of key facts, then the photos in one pewter frame. Phones show the photo first (one swipeable photo with a back
 * arrow), then the name; from `lg` up the name leads and the photos become
 * the mosaic.
 */
export function ActivityPageTop(props: ActivityPageTopProps) {
  const { t } = useTranslation();
  const { highlight, subtitleUsed } = splitTopFacts(props);
  const [localSaved, setLocalSaved] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const signedInSave = props.onToggleWishlist !== undefined;

  // The pill bar appears once the name has scrolled up out of view.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    const el = titleRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry) setPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // This browser's saved list, for guests who are not signed in. Read after
  // mount only: the server has no localStorage and must render "Save".
  useEffect(() => {
    if (signedInSave) return;
    const sync = () => setLocalSaved(readIsSaved(props.slug));
    sync();
    const onStorage = (e: StorageEvent) => {
      if (e.key === SAVED_STORAGE_KEY) sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(SAVED_CHANGED_EVENT, sync as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SAVED_CHANGED_EVENT, sync as EventListener);
    };
  }, [props.slug, signedInSave]);

  const saved = signedInSave ? !!props.wishlisted : localSaved;

  const toggleSave = () => {
    if (signedInSave) {
      props.onToggleWishlist!();
      return;
    }
    setLocalSaved(
      toggleSavedItem({
        slug: props.slug,
        title: props.title,
        city: props.cityName,
        price: props.price,
        currency: "MAD",
        priceType: props.priceType,
        image: props.images[0],
        category: props.categoryName ?? undefined,
      }),
    );
  };
  // Toasts "Saved to your list" (with Undo) or "Removed from your list" once
  // the press has really changed the state.
  const onSave = useSaveToast(saved, toggleSave);

  /* The phone's own share sheet when there is one; the page's share modal
     (copy link, WhatsApp, Facebook, Messages) when there is not, or when the
     sheet refuses to open, as it does inside in-app browsers. A cancel is
     left alone. */
  const onShare = useCallback(async () => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: props.title, text: props.title, url });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
      }
    }
    setShareUrl(url);
    setShareOpen(true);
  }, [props.title]);

  const paymentLabel =
    props.payment === "free"
      ? t("activity.table_promise_short", { defaultValue: "Free to reserve" })
      : props.payment === "online"
      ? t("activity.pay_online_in_full", { defaultValue: "Pay online, in full" })
      : null;

  const factsLine: Array<{ key: string; node: ReactNode } | null> = [
    props.cityName ? { key: "place", node: <span>{props.cityName}</span> } : null,
    highlight ? { key: "highlight", node: <span>{highlight}</span> } : null,
    paymentLabel
      ? {
          key: "payment",
          node: (
            /* Kept whole: on a phone the line broke inside it ("Free to" /
               "reserve · No card"); now the promise moves down as one piece. */
            <span className="whitespace-nowrap font-semibold" style={{ color: PW.green }} data-testid="top-payment">
              {paymentLabel}
            </span>
          ),
        }
      : null,
  ];
  const factParts = factsLine.filter((p): p is { key: string; node: ReactNode } => p !== null);

  /* Back to wherever the guest came from, with its filters and dates, since
     the browser keeps that page's URL. A guest who landed here straight from
     a search engine has nowhere to go back to, so they get the city instead. */
  const onBack = () => {
    if (typeof window === "undefined") return;
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    const home = props.crumbs[0]?.href;
    const parent = [...props.crumbs].reverse().find((c) => c.href && c.href !== home);
    window.location.assign(parent?.href ?? home ?? "/");
  };

  const saveLabel = saved
    ? t("activity.saved", { defaultValue: "Saved" })
    : t("activity.save", { defaultValue: "Save" });
  const shareLabel = t("activity.share", { defaultValue: "Share" });

  return (
    <section className="flex flex-col pt-3 lg:pt-0" data-testid="activity-page-top">
      {/* Phones open on the photo, like the app and the old phone hero
          (owner, 2026-09-28); from lg up the name leads, as in the lab.
          CSS order only: the HTML keeps breadcrumbs and the h1 first. */}
      <div className="order-2 lg:order-1">
      <Breadcrumbs items={props.crumbs} className="mb-4 hidden md:flex" />

      <div className="flex items-start justify-between gap-3 sm:gap-6">
        <div className="min-w-0">
          {/* The page's only <h1>: the venue name. The city sits in the facts
              line, the breadcrumbs and the <title>. */}
          <h1 ref={titleRef} className="font-heading text-[26px] font-bold leading-[1.15] tracking-[-0.01em] text-[#110E39] md:text-[34px] lg:text-[38px]">
            {props.title}
          </h1>
          {factParts.length > 0 && (
            <p
              className="mt-1.5 font-sans text-[14px] leading-relaxed text-[#110E39] md:text-[15px]"
              data-testid="top-facts-line"
            >
              {/* Plain flowing text, so a phone wraps it like a sentence rather
                  than one part per line. The dot trails each part, so a
                  wrapped line never starts with a stray "·". */}
              {factParts.map((part, i) => (
                <span key={part.key}>
                  {part.node}
                  {i < factParts.length - 1 && (
                    <>
                      <span aria-hidden="true" className="mx-1.5 text-[#5a5d77]">
                        ·
                      </span>
                      {/* A break point after the dot: the spans touch, so a
                          part that never breaks inside (the payment promise)
                          had nowhere to go and ran off a French phone. */}
                      <wbr />
                    </>
                  )}
                </span>
              ))}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {props.onMenu && (
            <button
              type="button"
              onClick={props.onMenu}
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-4 font-sans text-[13.5px] font-semibold"
              style={CONTROL_STYLE}
              data-testid="top-menu"
            >
              <Icon name="ri-restaurant-line" className="text-[15px]" aria-hidden />
              {t("activity.menu", { defaultValue: "Menu" })}
            </button>
          )}
          {/* On phones and tablets Share and Save sit on the photo instead
              (owner, 2026-09-29), leaving this spot to the Menu button. */}
          <button
            type="button"
            onClick={onShare}
            aria-label={shareLabel}
            className="hidden h-10 min-w-10 items-center justify-center gap-1.5 rounded-full px-2.5 font-sans text-[13.5px] font-semibold sm:px-4 lg:inline-flex"
            style={CONTROL_STYLE}
          >
            <Icon name="ri-share-forward-line" className="text-[15px] rtl:-scale-x-100" aria-hidden />
            <span className="hidden sm:inline">{shareLabel}</span>
          </button>
          <button
            type="button"
            onClick={onSave}
            aria-pressed={saved}
            aria-label={saveLabel}
            className="hidden h-10 min-w-10 items-center justify-center gap-1.5 rounded-full px-2.5 font-sans text-[13.5px] font-semibold sm:px-4 lg:inline-flex"
            style={CONTROL_STYLE}
          >
            <Icon
              name={saved ? "ri-heart-3-fill" : "ri-heart-3-line"}
              className="text-[15px]"
              color={saved ? "#E0245E" : undefined}
              aria-hidden
            />
            <span className="hidden sm:inline">{saveLabel}</span>
          </button>
          {props.titleAction ? <div className="hidden lg:block">{props.titleAction}</div> : null}
        </div>
      </div>

      {showsSubtitle(props.title, props.subtitle) && !subtitleUsed && (
        <p className="mt-2 max-w-[640px] font-sans text-[14.5px] leading-relaxed text-[#5a5d77] md:text-[15.5px]">
          {props.subtitle}
        </p>
      )}

      {props.children}
      </div>

      <div ref={props.galleryRef} className="relative order-1 mb-4 lg:order-2 lg:mb-0 lg:mt-4">
        <ActivityGallery
          images={props.images}
          title={props.title}
          video={props.video}
          videoMeta={props.videoMeta}
          videoDescription={props.videoDescription}
          videoCta={props.videoCta}
          map={props.map}
          onBack={onBack}
          phoneActions={[
            {
              key: "save",
              label: saveLabel,
              icon: saved ? "ri-heart-3-fill" : "ri-heart-3-line",
              iconColor: saved ? "#E0245E" : undefined,
              pressed: saved,
              onPress: onSave,
              testId: "top-photo-save",
            },
            {
              key: "share",
              label: shareLabel,
              icon: "ri-share-forward-line",
              flipInRtl: true,
              onPress: () => {
                onShare();
              },
              testId: "top-photo-share",
            },
          ]}
        />
      </div>

      <PillBar
        pinned={pinned}
        title={props.title}
        photo={props.images[0]}
        saved={saved}
        saveLabel={saveLabel}
        onBack={onBack}
        onSave={onSave}
      />


      {shareOpen &&
        props.renderShareModal?.({
          url: shareUrl,
          title: props.title,
          thumbnail: props.images[0],
          cityName: props.cityName,
          price: props.price,
          onClose: () => setShareOpen(false),
        })}
    </section>
  );
}

/**
 * The bar the top of the page shrinks into on phones and tablets (owner,
 * 2026-10-27, as in the app): back, a navy pill with the photo and the name
 * that goes back to the top, and Save. Hidden until the name has scrolled
 * away; it slides in with transform and opacity only, and is inert while
 * hidden so it never catches a tap or a focus meant for the page.
 */
function PillBar(props: {
  pinned: boolean;
  title: string;
  photo?: string;
  saved: boolean;
  saveLabel: string;
  onBack: () => void;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  const { pinned } = props;
  return (
    <div
      inert={!pinned}
      aria-hidden={!pinned}
      data-testid="top-pill-bar"
      className="fixed inset-x-0 top-0 z-[var(--z-header,60)] border-b border-[rgba(11,15,77,0.08)] bg-white pt-[env(safe-area-inset-top)] transition-[opacity,transform] duration-200 ease-out lg:hidden"
      style={{
        opacity: pinned ? 1 : 0,
        transform: pinned ? "translateY(0)" : "translateY(-10px)",
        pointerEvents: pinned ? "auto" : "none",
      }}
    >
      <div className="mx-auto flex h-[60px] max-w-[760px] items-center gap-2.5 px-3.5">
        <button
          type="button"
          onClick={props.onBack}
          aria-label={t("common.back", { defaultValue: "Back" })}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={CONTROL_STYLE}
        >
          <Icon name="ri-arrow-left-line" className="text-[18px] rtl:-scale-x-100" aria-hidden />
        </button>

        <div className="flex min-w-0 flex-1 justify-center">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex min-h-10 py-1 min-w-0 max-w-full items-center gap-2 rounded-full pe-3 ps-[5px] text-white transition-transform delay-75 duration-300 ease-out active:scale-[0.96]"
            style={{ background: PW.navyGradient, transform: pinned ? "scale(1)" : "scale(0.85)" }}
            data-testid="top-pill"
          >
            {/* Round, like an avatar, with a white ring (owner, 2026-10-27). */}
            <span className="h-[30px] w-[30px] shrink-0 overflow-hidden rounded-full border-[1.5px] border-white bg-[#2a2660]">
              {props.photo ? (
                <img src={props.photo} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : null}
            </span>
            <span className="min-w-0 line-clamp-2 break-words leading-tight font-heading text-[14.5px] font-bold">{props.title}</span>
            <span className="sr-only">, {t("common.back_to_top", { defaultValue: "Back to top" })}</span>
            <Icon name="ri-arrow-up-s-line" className="shrink-0 text-[18px]" aria-hidden />
          </button>
        </div>

        <button
          type="button"
          onClick={props.onSave}
          aria-pressed={props.saved}
          aria-label={props.saveLabel}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={CONTROL_STYLE}
        >
          <Icon
            name={props.saved ? "ri-heart-3-fill" : "ri-heart-3-line"}
            className="text-[18px]"
            color={props.saved ? "#E0245E" : undefined}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}
